import {
  INBOX_NOTIFICATION_TYPE,
  type InboxNotification,
  type InboxNotificationPayload,
  type InboxNotificationType,
} from '../constants/notifications'
import {
  OPEN_ORDER_STATUSES,
  ORDER_STATUS,
  type Order,
} from '../constants/orders'
import { OVERVIEW_PAGE, type Metric } from '../constants/vendor'
import { formatCurrency } from './format'

export function isSameLocalDay(iso: string, now = new Date()): boolean {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return false
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  )
}

export function buildVendorOverviewMetrics(orders: Order[]): Metric[] {
  const todayOrders = orders.filter(
    (order) =>
      isSameLocalDay(order.createdAt) && order.status !== ORDER_STATUS.cancelled,
  )
  const sales = todayOrders.reduce((sum, order) => sum + order.total, 0)
  const openOrders = orders.filter((order) => OPEN_ORDER_STATUSES.includes(order.status))
  const toConfirm = openOrders.filter((order) => order.status === ORDER_STATUS.pending).length
  const deliveredToday = orders.filter(
    (order) =>
      order.status === ORDER_STATUS.delivered && isSameLocalDay(order.updatedAt),
  )

  return [
    {
      id: 'sales',
      label: OVERVIEW_PAGE.metricSales,
      value: formatCurrency(sales),
      hint: OVERVIEW_PAGE.hintOrders(todayOrders.length),
      hintTone: 'neutral',
    },
    {
      id: 'open-orders',
      label: OVERVIEW_PAGE.metricOpenOrders,
      value: String(openOrders.length),
      hint:
        toConfirm > 0
          ? OVERVIEW_PAGE.hintToConfirm(toConfirm)
          : OVERVIEW_PAGE.hintInProgress,
      hintTone: toConfirm > 0 ? 'warn' : 'neutral',
    },
    {
      id: 'delivered',
      label: OVERVIEW_PAGE.metricDelivered,
      value: String(deliveredToday.length),
      hint: OVERVIEW_PAGE.hintCompleted,
      hintTone: 'neutral',
    },
    {
      id: 'today',
      label: OVERVIEW_PAGE.metricToday,
      value: String(todayOrders.length),
      hint: OVERVIEW_PAGE.hintPlaced,
      hintTone: 'neutral',
    },
  ]
}

export function isInboxNotificationType(value: string): value is InboxNotificationType {
  return (
    value === INBOX_NOTIFICATION_TYPE.orderAssigned ||
    value === INBOX_NOTIFICATION_TYPE.inquiryCreated
  )
}

export function mapInboxNotification(raw: Record<string, unknown>): InboxNotification | null {
  const id = typeof raw.id === 'string' ? raw.id : ''
  const type = typeof raw.type === 'string' ? raw.type : ''
  if (!id || !isInboxNotificationType(type)) return null
  const payloadRaw =
    raw.payload && typeof raw.payload === 'object' && !Array.isArray(raw.payload)
      ? (raw.payload as Record<string, unknown>)
      : {}
  const payload: InboxNotificationPayload = {}
  if (typeof payloadRaw.orderId === 'string') payload.orderId = payloadRaw.orderId
  if (typeof payloadRaw.orderNumber === 'string') payload.orderNumber = payloadRaw.orderNumber
  if (typeof payloadRaw.inquiryId === 'string') payload.inquiryId = payloadRaw.inquiryId
  return {
    id,
    type,
    title: typeof raw.title === 'string' ? raw.title : '',
    body: typeof raw.body === 'string' ? raw.body : '',
    payload,
    readAt: typeof raw.readAt === 'string' ? raw.readAt : null,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
  }
}
